/**
 * Mise en page du formulaire slide admin : ordre des champs, grilles 2 colonnes,
 * libellés breakpoint 768px pour les fonds, champs rendus ailleurs (skipKeys).
 */
import type { ItemDefinition } from '@admin/pages/personalization/content-block-slides.ts';

/** Ordre d’affichage des champs connus ; le reste suit dans l’ordre API. */
export const FIELD_SECTION_ORDER = [
  'description',
  'text_cta',
  'extern_link',
  'text_color',
  'cta_position',
  'background_image',
  'filter_image'
] as const;

/** Descriptions widget (`itemDescription`) affichées en tooltip ℹ️ plutôt qu sous le champ. */
export const FIELD_HINT_IN_TOOLTIP = new Set([
  'description',
  'text_cta',
  'extern_link',
  'text_color',
  'cta_position',
  'background_image',
  'filter_image'
]);

/** Détecte `background_image_mobile` (clé ou libellé contenant « mobile »). */
export const isMobileBackgroundImageDefinition = (def: ItemDefinition): boolean =>
  def.itemType === 'media' &&
  def.itemKey !== 'background_image' &&
  (/mobile/i.test(def.itemKey) || /mobile/i.test(def.itemLabel));

/** Libellé affiché (breakpoint desktop / mobile pour les fonds). */
export const getSlideFieldDisplayLabel = (
  itemDefinition: ItemDefinition,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (itemDefinition.itemKey === 'background_image') {
    return t('admin.contentblock.editor.background_image.label', {
      label: itemDefinition.itemLabel
    });
  }

  if (isMobileBackgroundImageDefinition(itemDefinition)) {
    return t('admin.contentblock.editor.background_image_mobile.label', {
      label: itemDefinition.itemLabel
    });
  }

  return itemDefinition.itemLabel;
};

/** Trie les définitions : d’abord {@link FIELD_SECTION_ORDER}, puis champs additionnels API. */
export const orderItemDefinitions = (itemDefinitions: ItemDefinition[]): ItemDefinition[] => {
  const definitionByKey = new Map(itemDefinitions.map((def) => [def.itemKey, def]));

  return [
    ...FIELD_SECTION_ORDER.flatMap((key) => {
      const def = definitionByKey.get(key);
      return def ? [def] : [];
    }),
    ...itemDefinitions.filter(
      (def) => !FIELD_SECTION_ORDER.includes(def.itemKey as (typeof FIELD_SECTION_ORDER)[number])
    )
  ];
};

/**
 * Prépare le rendu du formulaire : paires CTA/lien, couleur/position, fonds desktop+mobile.
 * `skipKeys` évite de dupliquer les champs déjà placés dans une grille dédiée.
 */
export const getSlideFormLayoutDefinitions = (itemDefinitions: ItemDefinition[]) => {
  const definitionByKey = new Map(itemDefinitions.map((def) => [def.itemKey, def]));
  const backgroundImageMobileDef = itemDefinitions.find(isMobileBackgroundImageDefinition);

  const skipKeys = new Set<string>(['description']);
  const textCtaDef = definitionByKey.get('text_cta');
  const externLinkDef = definitionByKey.get('extern_link');
  const textColorDef = definitionByKey.get('text_color');
  const ctaPositionDef = definitionByKey.get('cta_position');
  const filterDef = definitionByKey.get('filter_image');
  const backgroundImageDef = definitionByKey.get('background_image');

  if (textCtaDef) skipKeys.add('text_cta');
  if (externLinkDef) skipKeys.add('extern_link');
  if (textColorDef) skipKeys.add('text_color');
  if (ctaPositionDef) skipKeys.add('cta_position');
  if (filterDef) skipKeys.add('filter_image');
  if (backgroundImageDef) skipKeys.add('background_image');
  if (backgroundImageMobileDef) skipKeys.add(backgroundImageMobileDef.itemKey);

  return {
    definitionByKey,
    descriptionDef: definitionByKey.get('description'),
    textCtaDef,
    externLinkDef,
    textColorDef,
    ctaPositionDef,
    filterDef,
    backgroundImageDef,
    backgroundImageMobileDef,
    skipKeys,
    orderedDefinitions: orderItemDefinitions(itemDefinitions)
  };
};
